import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { WATERMARK_TYPE } from '@nrwl/rx-models';
import { DomSanitizer } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RxIconModule } from '@nrwl/rx-icon';

@Component({
  selector: 'watermark-preview-settings',
  templateUrl: './watermark-preview-settings.component.html',
  styleUrls: ['./watermark-preview-settings.component.scss'],
  imports: [CommonModule, FormsModule, RxIconModule],
})
export class WatermarkPreviewSettingsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() data: any;

  @ViewChild('watermarkContainer', { static: false }) watermarkContainer: ElementRef;
  @ViewChild('watermarkElement', { static: false }) watermarkElement: ElementRef;
  @ViewChild('textContainer', { static: false }) textContainer: ElementRef;

  @ViewChild('screen', { static: false }) screen: ElementRef;

  private _clinicLogo: any;
  private _backgroundSrc: any = '';

  public previewPositionStyles;
  public uploadedImageSrc;
  public backgroundAspectRatio = 0;
  public currentFontSize = 20;
  public defaultPadding = 0;
  public textPadding = 0;
  private widthPoint = 1;
  private heightPoint = 1;
  public showContent = false;
  public imgSize = {
    height: 'auto',
    width: 80 + 'px',
  };

  public get clinicLogo() {
    return this._clinicLogo;
  }
  @Input() public set clinicLogo(value) {
    this._clinicLogo = value;
  }

  public get backgroundSrc() {
    return this._backgroundSrc;
  }
  @Input() public set backgroundSrc(value) {
    this._backgroundSrc = value;
  }

  WATERMARK_TYPE = WATERMARK_TYPE;

  constructor(
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit() {
    this.watermarkImgStyles = this.sanitizer.bypassSecurityTrustStyle(`width: 0px; height: 0px;`);
    this.setStylesAccordingPositions();
  }

  ngAfterViewInit(): void {
    const img = new Image();
    img.src = this.backgroundSrc;
    img.onload = (e) => {
      this.calculateSizes(img);
    };
    img.onerror = () => {
      const imgDefault = new Image();
      imgDefault.src = 'assets/images/img-test.png';
      imgDefault.onload = (e) => {
        this.calculateSizes(imgDefault);
      };
    };

    this.uploadedImageSrc =
      (this.data.watermark_image as any) instanceof File
        ? this.getFileSrc(this.data.watermark_image)
        : this.data.watermark_image;

    this.cdr.detectChanges();

    this.setFontSize();
  }

  public watermarkImgStyles;

  private calculateSizes(img) {
    this.backgroundAspectRatio = img.height / img.width;
    this.watermarkContainer.nativeElement.style.height =
      this.watermarkContainer.nativeElement.offsetWidth * this.backgroundAspectRatio + 'px';
    this.getWatermarkSizesAccordingToImg(img);

    this.widthPoint = this.watermarkContainer.nativeElement.clientWidth / 100;
    this.heightPoint = this.watermarkContainer.nativeElement.clientHeight / 100;

    const styles = ` ${this.previewPositionStyles[this.data.watermark_position]}
                            ${this.getFontFamily(this.data.watermark_font)}`;

    this.watermarkElement.nativeElement.style.cssText = styles;

    this.setWatermarkElementStyles();

    this.showContent = true;
    this.cdr.detectChanges();
  }

  private resizeObserver: ResizeObserver;

  private setWatermarkElementStyles() {
    const sizes = `width: ${this.imgSize.width}; height: ${this.imgSize.height}; font-size: 0 !important;`;
    const fontSize = `font-size: ${this.currentFontSize}px;`;
    this.watermarkImgStyles = this.sanitizer.bypassSecurityTrustStyle(
      this.getPaddingStyles() +
        (this.data.watermark_type === WATERMARK_TYPE.TEXT ? fontSize : sizes),
    );
  }

  private setFontSize() {
    this.resizeObserver = new ResizeObserver(() => {
      const containerWidth = this.watermarkContainer.nativeElement.clientWidth;
      this.currentFontSize = containerWidth / 20;
      this.cdr.detectChanges();
    });
    this.resizeObserver.observe(this.watermarkContainer.nativeElement);
  }

  public ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver?.disconnect();
    }
  }

  private getFileSrc(file: File) {
    return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));
  }

  private getPaddingStyles() {
    return `
         margin-bottom: ${
           this.data.watermark_horizontal_nudge_type === 1
             ? this.data.watermark_position === 4
               ? this.data.watermark_horizontal_nudge * 2
               : this.data.watermark_horizontal_nudge * this.heightPoint
             : 0
         }px;
         margin-top: ${
           this.data.watermark_horizontal_nudge_type === 0
             ? this.data.watermark_position === 4
               ? this.data.watermark_horizontal_nudge * 2
               : this.data.watermark_horizontal_nudge * this.heightPoint
             : 0
         }px;
         margin-right: ${
           this.data.watermark_vertical_nudge_type === 0
             ? this.data.watermark_position === 4
               ? this.data.watermark_vertical_nudge * 2
               : this.data.watermark_vertical_nudge * this.widthPoint
             : 0
         }px;
         margin-left: ${
           this.data.watermark_vertical_nudge_type === 1
             ? this.data.watermark_position === 4
               ? this.data.watermark_vertical_nudge * 2
               : this.data.watermark_vertical_nudge * this.widthPoint
             : 0
         }px;
         `;
  }

  private setStylesAccordingPositions() {
    const type = this.data.watermark_type;
    this.previewPositionStyles = {
      0: `top: ${type === 2 ? this.textPadding : this.defaultPadding}px; left: ${
        type === 2 ? this.textPadding : this.defaultPadding
      }px; bottom: unset; right: unset; transform: none;`,
      1: `top: 50%; left: ${
        this.defaultPadding
      }px; bottom: unset; right: unset; transform: ${'translateY(-50%)'};`,
      2: `top: unset; left: ${type === 2 ? 0 : this.defaultPadding}px; bottom: ${
        type === 2 ? this.textPadding : this.defaultPadding
      }px; right: unset; transform: none;`,
      3: `top: ${
        type === 2 ? this.textPadding : this.defaultPadding
      }px; left: 50%; bottom: unset; right: unset; transform: translateX(-50%);`,
      4: `top: 50%; left: 50%; transform: translate(-50%, -50%); bottom: unset; right: unset;`,
      5: `top: unset; left: 50%; bottom: ${
        type === 2 ? this.textPadding : this.defaultPadding
      }px; right: unset; transform: translateX(-50%);`,
      6: `top: ${type === 2 ? this.textPadding : this.defaultPadding}px; left: unset; bottom: unset; right: ${
        type === 2 ? this.textPadding : this.defaultPadding
      }px; transform: none;`,
      7: `top: 50%; left: unset; bottom: unset; right: ${
        type === 2 ? this.textPadding : this.defaultPadding
      }px; transform: ${'translateY(-50%)'};`,
      8: `top: unset; left: unset; bottom: ${type === 2 ? this.textPadding : this.defaultPadding}px; right: ${
        type === 2 ? this.textPadding : this.defaultPadding
      }px; transform: none;`,
    };
  }

  private getFontFamily(value) {
    switch (value) {
      case 0:
        return `font-family: 'Montserrat', sans-serif;`;
      case 1:
        return `font-family: 'Oleo Script', cursive;`;
      default:
        return `font-family: 'Merriweather Sans', sans-serif;`;
    }
  }

  getWatermarkSizesAccordingToImg(image) {
    let imgWatermark: any = '';
    if (this.data.watermark_type === this.WATERMARK_TYPE.UPLOAD) {
      imgWatermark = this.data.watermark_image;
    }
    if (this.data.watermark_type === this.WATERMARK_TYPE.LOGO) {
      imgWatermark = this.clinicLogo;
    }

    if ((imgWatermark as any) instanceof File) {
      const reader = new FileReader();
      reader.addEventListener('load', (event: any) => {
        this.setImgSizeByWatermark(URL.createObjectURL(imgWatermark));
      });
      reader.readAsDataURL(this.data.watermark_image);
    } else {
      this.setImgSizeByWatermark(imgWatermark);
    }
  }

  private setImgSizeByWatermark(src) {
    const containerWidth = this.watermarkContainer.nativeElement.clientWidth;
    const containerHeight = this.watermarkContainer.nativeElement.clientHeight;

    const img = new Image();
    img.src = src;
    img.onload = (e) => {
      if (img.width >= img.height) {
        this.imgSize.width = containerWidth / 4 + 'px';
        this.imgSize.height = 'auto';
      }
      if (img.width < img.height) {
        this.imgSize.height = containerHeight / 4 + 'px';
        this.imgSize.width = 'auto';
      }
      this.setWatermarkElementStyles();
      this.cdr.detectChanges();
    };
  }
}
